import {
  Box,
  Toolbar,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
  Collapse,
} from '@mui/material';
import {
  Dashboard,
  ListAlt,
  Send as SendIcon,
  Backup as BackupIcon,
  AttachMoney as PriceIcon,
  HelpOutline as FaqIcon,
  Headphones as HeadphonesIcon, // 🎧 웨어러블 아이콘 추가
  Storage as StorageIcon, // 📦 매입 데이터 백업 아이콘
  LocalOffer as LocalOfferIcon, // 🏷️ 가격 데이터 백업 아이콘
  Science as ScienceIcon, // 🧪 SSOT 테스트 랩 아이콘
  Campaign as CampaignIcon, // 📢 팝업 관리 아이콘
  Notifications as NotificationsIcon, // 📢 공지사항 관리 아이콘
  QueryStats as QueryStatsIcon, // 📈 유입경로 통계 아이콘
} from '@mui/icons-material';
import TabletMacIcon from '@mui/icons-material/TabletMac';
import { useLocation, useNavigate } from 'react-router-dom';
import { ExpandLess, ExpandMore, Smartphone } from '@mui/icons-material';
import { useEffect, useState } from 'react';

interface SidebarProps {
  onNavigate?: () => void; // 모바일에서 메뉴 클릭 시 Drawer 닫기용
}

const MENU = [
  {
    key: 'dashboard',
    path: '/admin/dashboard',
    icon: <Dashboard />,
    label: '대시보드',
  },
  {
    key: 'orders',
    path: '/admin/orders',
    icon: <ListAlt />,
    label: '신청 목록',
  },
  {
    key: 'inflow-stats',
    path: '/admin/inflow-stats',
    icon: <QueryStatsIcon />,
    label: '유입경로 통계',
  },
  {
    key: 'alerts',
    path: '/admin/alerts',
    icon: <SendIcon />,
    label: '알림톡 전송',
  },
  {
    key: 'price-management',
    path: '/admin/price-management',
    icon: <PriceIcon />,
    label: '가격 관리',
    description: '등급별 매입가격 설정',
    children: [
      {
        key: 'price-phone',
        label: '스마트폰',
        icon: <Smartphone fontSize="small" />,
        children: [
          { key: 'price-phone-apple', path: '/admin/price-management?type=스마트폰&brand=Apple', icon: <Smartphone fontSize="small" />, label: '애플' },
          { key: 'price-phone-samsung', path: '/admin/price-management?type=스마트폰&brand=Samsung', icon: <Smartphone fontSize="small" />, label: '삼성' },
          { key: 'price-phone-lg', path: '/admin/price-management?type=스마트폰&brand=LG', icon: <Smartphone fontSize="small" />, label: 'LG' },
        ]
      },
      {
        key: 'price-tablet',
        label: '태블릿',
        icon: <TabletMacIcon fontSize="small" />,
        children: [
          { key: 'price-tablet-apple', path: '/admin/price-management?type=태블릿&brand=Apple', icon: <TabletMacIcon fontSize="small" />, label: '애플' },
          { key: 'price-tablet-samsung', path: '/admin/price-management?type=태블릿&brand=Samsung', icon: <TabletMacIcon fontSize="small" />, label: '삼성' },
        ]
      },
      // 🎧 웨어러블 메뉴 추가
      {
        key: 'price-wearable',
        label: '웨어러블',
        icon: <HeadphonesIcon fontSize="small" />,
        children: [
          { key: 'price-wearable-apple', path: '/admin/price-management?type=웨어러블&brand=Apple', icon: <HeadphonesIcon fontSize="small" />, label: '애플(AirPods)' },
        ]
      }
    ]
  },
  {
    key: 'backup',
    path: '/admin/backup',
    icon: <BackupIcon />,
    label: '백업 & 복원',
    description: '데이터 백업 및 복원 관리',
    children: [
      { 
        key: 'backup-application', 
        path: '/admin/backup?type=application', 
        icon: <StorageIcon fontSize="small" />, 
        label: '매입 데이터' 
      },
      { 
        key: 'backup-price', 
        path: '/admin/backup?type=price', 
        icon: <LocalOfferIcon fontSize="small" />, 
        label: '가격 데이터' 
      },
    ]
  },
  {
    key: 'popup-management',
    path: '/admin/popup',
    icon: <CampaignIcon />,
    label: '팝업 관리',
  },
  {
    key: 'notices',
    path: '/admin/notices',
    icon: <NotificationsIcon />,
    label: '공지사항 관리',
    description: '공지사항 등록 및 관리',
  },
  {
    key: 'faq',
    path: '/admin/faq',
    icon: <FaqIcon />,
    label: 'FAQ 관리',
    description: '자주 묻는 질문 관리',
  },
  {
    key: 'ssot-test-lab',
    path: '/admin/ssot-test-lab',
    icon: <ScienceIcon />,
    label: '테스트 관리',
    description: 'ID 체계 정합성 검증',
  },

];

const Sidebar = ({ onNavigate }: SidebarProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [openMenus, setOpenMenus] = useState<Record<string, boolean>>({});

  const handleNavigate = (path: string) => {
    navigate(path);
    if (onNavigate) onNavigate();
  };

  // 현재 라우트에 따라 기본 확장 상태 설정
  useEffect(() => {
    if (location.pathname.startsWith('/admin/price-management')) {
      setOpenMenus(prev => ({ ...prev, 'price-management': true }));
    }
    if (location.pathname.startsWith('/admin/backup')) {
      setOpenMenus(prev => ({ ...prev, 'backup': true }));
    }
  }, [location.pathname]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: '#fbfbfc',
        display: 'flex',
        flexDirection: 'column',
        px: 1,
        py: 1,
      }}
    >
      {/* 로고 + 브랜드명 */}
      <Toolbar
        sx={{
          justifyContent: 'center',
          cursor: 'pointer',
          mb: 1.5,
        }}
        onClick={() => handleNavigate('/admin/dashboard')}
      >
        <Box
          component="img"
          src="/assets/dwt_logo.png"
          alt="로고"
          sx={{ height: 40, mb: 0.5, mr: 1 }}
        />
        <Typography
          variant="h6"
          fontWeight={900}
          sx={{
            display: { xs: 'none', md: 'block' },
            color: '#1976d2',
            letterSpacing: '-1.5px',
            ml: 1,
            fontSize: 22,
          }}
        >
          DawonTrade
        </Typography>
      </Toolbar>
      <Divider sx={{ mb: 1.5, borderColor: '#e0e7ef' }} />

      <List sx={{ p: 0 }}>
        {MENU.map((item) => {
          const selected = location.pathname.startsWith(item.path);
          const hasChildren = Array.isArray((item as any).children) && (item as any).children.length > 0;
          const isPriceMenu = item.key === 'price-management';
          const expanded = hasChildren ? (openMenus[item.key] ?? selected) : false;
          return (
            <Box key={item.key}>
              <ListItem
                button
                onClick={() => {
                  if (hasChildren) {
                    setOpenMenus(prev => ({ ...prev, [item.key]: !expanded }));
                    return;
                  }
                  handleNavigate(item.path);
                }}
                sx={{
                  my: 0.3,
                  borderRadius: 2.5,
                  px: 2,
                  bgcolor: selected ? '#e3f2fd' : 'transparent',
                  boxShadow: selected ? '0 2px 12px #1976d233' : undefined,
                  transition: 'all 0.12s',
                  '&:hover': { bgcolor: '#f6faff' },
                }}
                selected={selected}
              >
                <ListItemIcon sx={{ minWidth: 40, color: selected ? '#1976d2' : '#b0b7c3', fontSize: 22 }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography fontWeight={selected ? 800 : 600} fontSize={16} color={selected ? '#1976d2' : '#444'}>
                      {item.label}
                    </Typography>
                  }
                />
                {hasChildren ? (expanded ? <ExpandLess /> : <ExpandMore />) : null}
              </ListItem>

              {hasChildren && (
                <Collapse in={expanded} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {(item as any).children.map((child: any) => {
                      const level2HasChildren = Array.isArray(child.children) && child.children.length > 0;
                      const level2Key = child.key;
                      const level2Expanded = openMenus[level2Key] ?? false;
                      
                      // 🔥 하위 메뉴 선택 상태 계산 (가격 관리 + 백업 메뉴 지원)
                      let isLevel2Selected = false;
                      if (item.key === 'price-management') {
                        isLevel2Selected = location.pathname.startsWith('/admin/price-management') &&
                          new URLSearchParams(location.search).get('type') === (child.label === '스마트폰' ? '스마트폰' : child.label === '태블릿' ? '태블릿' : '웨어러블');
                      } else if (item.key === 'backup' && child.path) {
                        // 백업 메뉴: URL 파라미터로 선택 상태 확인
                        const childParams = new URLSearchParams(child.path.split('?')[1] || '');
                        const childType = childParams.get('type');
                        const currentType = new URLSearchParams(location.search).get('type');
                        isLevel2Selected = location.pathname.startsWith('/admin/backup') && currentType === childType;
                      }
                      
                      return (
                        <Box key={child.key}>
                          <ListItem
                            button
                            sx={{ pl: 4, py: 0.6, borderRadius: 2, mx: 1, my: 0.2, bgcolor: isLevel2Selected ? '#eef6ff' : 'transparent' }}
                            onClick={() => {
                              if (level2HasChildren) {
                                setOpenMenus(prev => ({ ...prev, [level2Key]: !level2Expanded }));
                                return;
                              }
                              // 🔥 직접 path가 있는 경우 네비게이션 (백업 메뉴 등)
                              if (child.path) {
                                handleNavigate(child.path);
                              }
                            }}
                          >
                            <ListItemIcon sx={{ minWidth: 32, color: isLevel2Selected ? '#1976d2' : '#90a4ae' }}>{child.icon}</ListItemIcon>
                            <ListItemText primary={<Typography fontSize={14} fontWeight={700}>{child.label}</Typography>} />
                            {level2HasChildren ? (level2Expanded ? <ExpandLess /> : <ExpandMore />) : null}
                          </ListItem>

                          {level2HasChildren && (
                            <Collapse in={level2Expanded} timeout="auto" unmountOnExit>
                              <List component="div" disablePadding>
                                {child.children.map((leaf: any) => {
                                  const curParams = new URLSearchParams(location.search);
                                  const curBrand = (curParams.get('brand') || '').toString();
                                  const curType = (curParams.get('type') || '').toString();
                                  const q = String(leaf.path || '').split('?')[1] || '';
                                  const leafParams = new URLSearchParams(q);
                                  const leafBrand = (leafParams.get('brand') || '').toString();
                                  const leafType = (leafParams.get('type') || '').toString();
                                  const leafSelected = location.pathname.startsWith('/admin/price-management') && curBrand === leafBrand && curType === leafType;
                                  return (
                                    <ListItem
                                      key={leaf.key}
                                      button
                                      sx={{ pl: 7, py: 0.5, borderRadius: 2, mx: 1, my: 0.2, bgcolor: leafSelected ? '#e3f2fd' : 'transparent' }}
                                      onClick={() => handleNavigate(leaf.path)}
                                      selected={leafSelected}
                                    >
                                      <ListItemIcon sx={{ minWidth: 28, color: leafSelected ? '#1976d2' : '#90a4ae' }}>{leaf.icon}</ListItemIcon>
                                      <ListItemText primary={<Typography fontSize={13} fontWeight={leafSelected ? 800 : 600} color={leafSelected ? '#1976d2' : 'inherit'}>{leaf.label}</Typography>} />
                                    </ListItem>
                                  );
                                })}
                              </List>
                            </Collapse>
                          )}
                        </Box>
                      );
                    })}
                  </List>
                </Collapse>
              )}
            </Box>
          );
        })}
      </List>
    </Box>
  );
};

export default Sidebar;
